import {Exclude, Expose, instanceToPlain, plainToInstance} from "class-transformer";
import * as t from "io-ts";
import {validateJSON, validateJSONNow} from "../../web/validation";
import {Entity} from "../entity";
import {FieldMatch} from "../search/search";
import {SortFieldSpec} from "../search/sort-field-spec";
import {PageSpec} from "../search/page-spec";
import {ServiceEventType} from "./service-event-type";

export const LocationStatusFacetCodec = t.intersection([
  t.type({
    id: t.string,
    siteId: t.string,
    serviceTypeId: t.string,
    statusFacet: t.string,
    status: t.string,
    asOfDttm: t.number,
    source: t.string,
    serviceMode: t.string,
    confidencePct: t.number,
    createDttm: t.number,
    lastModifiedDttm: t.number,
  }),
  t.partial({
    serviceEventId: t.string,
    note: t.string,
    createdById: t.string,
    lastModifiedById: t.string,

    siteName: t.string,
    siteLatCoord: t.number,
    siteLongCoord: t.number,
    siteGeofenceExtension: t.number,
    serviceTypeName: t.string,
    serviceEventName: t.string,
  }),
]);

export type LocationStatusFacet = t.TypeOf<typeof LocationStatusFacetCodec>;

@Exclude()
export class LocationStatusFacetEntity implements Entity {
  @Expose()
  id!: string;

  @Expose()
  siteId!: string;

  @Expose()
  siteName?: string;

  @Expose()
  siteLatCoord?: number;

  @Expose()
  siteLongCoord?: number;

  @Expose()
  siteGeofenceExtension?: number;

  @Expose()
  serviceTypeId!: string;

  @Expose()
  serviceTypeName?: string;

  @Expose()
  serviceEventId?: string;

  @Expose()
  serviceEventName?: string;

  @Expose()
  statusFacet!: string;

  @Expose()
  status!: string;

  @Expose()
  asOfDttm!: number;

  @Expose()
  source?: string;

  @Expose()
  serviceMode!: string;

  @Expose()
  confidencePct!: number;

  @Expose()
  note?: string;

  @Expose()
  createdById?: string;

  @Expose()
  lastModifiedById?: string;

  @Expose()
  createDttm!: number;

  @Expose()
  lastModifiedDttm!: number;

  readonly entityType = 'LOCSTATUSFACET';

  get displayName(): Readonly<string> {
    return this.id;
  }

  /**
   * Constructor for LocationStatusFacetEntity. Recommend using the {@link create} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(value: LocationStatusFacet): LocationStatusFacetEntity {
    return plainToInstance(LocationStatusFacetEntity, value, {
      excludeExtraneousValues: true,
    });
  }

  loadTypeValue(): Promise<LocationStatusFacet> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(LocationStatusFacetCodec, object);
  }

  asTypeValue(): LocationStatusFacet {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(LocationStatusFacetCodec, object);
  }
}

export interface LocationStatusFacetAtTimeParams {
  dttm?: number,
  serviceEventType?: string
  serviceEventId?: string
  statusTypeId?: string
  statusFacet?: string
  status?: string
  serviceMode?: string
}
